import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBook, getCollagesForBook, getRelatedDiaryEntries } from '../../data';
import './BookPage.css';
import Breadcrumbs from '../common/Breadcrumbs';
import Book3D from '../common/Book3D';
import NotFound from '../common/NotFound';
import { getImagePaths, getBestImagePath, shouldUseThumbnail } from '../../utils/imageUtils';
import { takeBookLaunch } from '../common/bookTransition';
import useDocumentMeta from '../../hooks/useDocumentMeta';

/** The pose the book comes to rest at, and how long it takes to get there. */
const RESTING = 'rotateX(0deg) rotateY(-24deg) rotateZ(0deg)';
const FLIGHT_MS = 950;
const FLIGHT_EASING = 'cubic-bezier(0.16, 0.84, 0.24, 1)';

const BookPage: React.FC = () => {
    const { id } = useParams();
    const book = getBook(id || '');
    const [isVisible, setIsVisible] = useState(false);
    const [arriving, setArriving] = useState(false);
    const heroRef = useRef<HTMLDivElement>(null);

    const paths = getImagePaths(book?.coverImage || '');
    const imagePath = getBestImagePath(paths, shouldUseThumbnail('detail'));

    useDocumentMeta({
        title: book && `${book.title} (${book.publishYear})`,
        description: book?.description,
        image: paths.thumbnailWebp,
        type: 'article'
    });

    useEffect(() => {
        setIsVisible(true);
    }, []);

    /* One movement, continuous with the library: the book carries its own
       position and pose across the route change and flies from there to
       here. Laid out before paint, so the first frame the reader sees is
       already the book where they left it rather than where it lands. */
    useLayoutEffect(() => {
        const launch = takeBookLaunch(book?.id || '');
        const hero = heroRef.current;
        if (!launch || !hero) return;

        const object = hero.querySelector<HTMLElement>('.book3d');
        const body = hero.querySelector<HTMLElement>('.book3d__body');
        if (!object || !body) return;

        /* ScrollToTop resets the page in an ordinary effect, which runs
           after this one — so measuring now would give a box from the
           previous scroll position. Resetting here first is idempotent. */
        window.scrollTo(0, 0);

        const rect = object.getBoundingClientRect();
        const timing: KeyframeAnimationOptions = {
            duration: FLIGHT_MS,
            easing: FLIGHT_EASING,
            /* Backwards fill is what keeps the book at its launch pose for
               the frames before the animation's start time resolves. */
            fill: 'both'
        };

        object.animate(
            [
                {
                    transform: `translate(${launch.left - rect.left}px, ${
                        launch.top - rect.top
                    }px)`
                },
                { transform: 'translate(0px, 0px)' }
            ],
            timing
        );

        body.animate([{ transform: launch.transform }, { transform: RESTING }], timing);

        /* The page's own fade would have the book flying in through it.
           Arriving skips it: this state lands before the first paint. */
        setArriving(true);
        setIsVisible(true);
    }, [book?.id]);

    if (!book) return <NotFound thing="book" backTo={{ label: 'the library', path: '/books' }} />;

    const relatedCollages = getCollagesForBook(book.id);
    const diaryEntries = getRelatedDiaryEntries(book.diaryIds);
    const place = [book.location?.city, book.location?.country].filter(Boolean).join(', ');

    return (
        <div
            className={`book-page ${isVisible ? 'visible' : ''} ${
                arriving ? 'arrived' : ''
            }`}
        >
            <Breadcrumbs
                items={[
                    { label: 'library', path: '/books' },
                    { label: book.title }
                ]}
            />

            <div className="book-hero">
                <div className="book-hero__object" ref={heroRef}>
                    <Book3D cover={imagePath} title={book.title} author={book.author} />
                </div>

                {/* Everything about the book reads down one column beside
                    it: what it is, then where it came from, then where it
                    turns up in the work. */}
                <div className={`book-hero__text ${arriving ? 'arriving' : ''}`}>
                    <h1 className="book-hero__title">{book.title}</h1>
                    <p className="book-hero__byline">
                        {book.author}
                        <span className="book-hero__year">{book.publishYear}</span>
                    </p>

                    <hr className="book-hero__rule" />

                    <p className="book-hero__description">{book.description}</p>

                    {book.location && (
                        <section className="book-block">
                            <h2 className="book-block__heading">Found at</h2>
                            <p className="book-block__lead">{book.location.name}</p>
                            {place && <p className="book-block__note">{place}</p>}
                        </section>
                    )}

                    {relatedCollages.length > 0 && (
                        <section className="book-block">
                            <h2 className="book-block__heading">Featured in</h2>
                            <ul className="book-rows">
                                {relatedCollages.map(collage => (
                                    <li key={collage.id}>
                                        <Link to={`/collages/${collage.id}`} className="book-row">
                                            <span className="book-row__name">{collage.title}</span>
                                            <span className="book-row__meta">{collage.date}</span>
                                            <span className="book-row__arrow" aria-hidden="true">
                                                ↗
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {diaryEntries.length > 0 && (
                        <section className="book-block">
                            <h2 className="book-block__heading">Diary mentions</h2>
                            <ul className="book-rows">
                                {diaryEntries.map(entry => (
                                    <li key={entry.id}>
                                        <Link to={`/diary/${entry.id}`} className="book-row">
                                            <span className="book-row__name">{entry.title}</span>
                                            <span className="book-row__meta">{entry.date}</span>
                                            <span className="book-row__arrow" aria-hidden="true">
                                                ↗
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookPage;
